import * as React from 'react';
import { Col, Row, Space } from 'antd';
import clsx from 'clsx';
import { Category, Section } from '../admin-knowledge-base.type';
import Controls from './controls';
import { getMenuItems } from '../admin-knowledge-base.utils';
import { PlusCircleOutlined } from '@ant-design/icons';
import { CSSProperties, memo } from 'react';
import Button from '/react4xp/mixins/ui/button/button';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragOutlined } from '@ant-design/icons';
import {validatePrivileges} from "mixins/helpers";

interface CategoryListProps {
    categories: Category[];
    children?: React.ReactNode;
    activeCategory: Category;
    activeSection: Section;
    selectCategory: (category: Category) => void;
    showUpdateForm: (category: Category) => void;
    showDeleteForm: (category: Category) => void;
    showCreateForm: (initials: {}) => void;
    handleReorder: (event: DragEndEvent) => void;
    roles: string[];
    privileges: string[];
}

interface SortableCategoryProps {
    category: Category;
    isActive: boolean;
    selectCategory: (category: Category) => void;
    showUpdateForm: (category: Category) => void;
    showDeleteForm: (category: Category) => void;
    roles: string[];
    privileges: string[];
}

const SortableCategory = ({
    category,
    isActive,
    selectCategory,
    showDeleteForm,
    showUpdateForm,
    roles,
    privileges
}: SortableCategoryProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } =
        useSortable({ id: category.id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        cursor: 'move',
        transition,
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleOnClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button') === e.currentTarget)
            selectCategory(category);
        scrollToTop();
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
        >
            <button
                key={category.id}
                className={clsx('button-card', 'button-card__category', {
                    'button-card__active': isActive,
                })}
                onClick={handleOnClick}
            >
                <DragOutlined
                    ref={setActivatorNodeRef}
                    style={{ cursor: 'move', fontSize: '1.25rem' }}
                    {...listeners}
                />
                {category.ru.title}
                <Controls
                    items={getMenuItems<Category>(
                        category,
                        showUpdateForm,
                        showDeleteForm,
                        roles,
                        privileges,
                        'PRIV_CATEGORY_EDIT',
                        'PRIV_CATEGORY_DELETE',
                    )}
                    noLabel
                />
            </button>
        </div>
    );
};

const CategoryList = memo(
    ({
        categories,
        children,
        activeCategory,
        activeSection,
        selectCategory,
        showUpdateForm,
        showCreateForm,
        showDeleteForm,
        handleReorder,
        roles,
        privileges,
    }: CategoryListProps) => {
        const sensors = useSensors(
            useSensor(PointerSensor),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            })
        );

        // const handleDragStart = (event) => {
        //     console.log(event);
        // };

        // const handleDragCancel = (event) => {
        //     console.log(event);
        // };

        const handleDragEnd = (event) => {
            handleReorder(event);
        };

        return (
            <Row
                gutter={20}
                wrap={false}
            >
                <Col flex="320px">
                    <Space
                        direction="vertical"
                        size={16}
                        style={{ display: 'flex' }}
                    >
                        {validatePrivileges(roles, privileges, 'PRIV_CATEGORY_CREATE') && (
                            <Button
                                icon={<PlusCircleOutlined />}
                                onClick={() => showCreateForm({ parent: activeSection.path })}
                                block
                                ghost
                            >
                                Добавить категорию
                            </Button>
                        )}
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            // onDragStart={handleDragStart}
                            // onDragCancel={handleDragCancel}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={categories.map((item) => item.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Space
                                    direction="vertical"
                                    size={16}
                                    style={{ display: 'flex' }}
                                >
                                    {categories.map((category) => (
                                        <SortableCategory
                                            roles={roles}
                                            privileges={privileges}
                                            key={category.id}
                                            category={category}
                                            isActive={activeCategory.id === category.id}
                                            selectCategory={selectCategory}
                                            showUpdateForm={showUpdateForm}
                                            showDeleteForm={showDeleteForm}
                                        />
                                    ))}
                                </Space>
                            </SortableContext>
                        </DndContext>
                    </Space>
                </Col>
                <Col flex="auto">{children}</Col>
            </Row>
        );
    }
);

export default CategoryList;
